import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { LanguageToggle } from '../shared/ui';
import { BuildingOfficeIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common']);
  const { login, isAuthenticated, loading, user } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const hasRedirected = React.useRef(false);

  React.useEffect(() => {
    if (isAuthenticated && user && !isLoading && !hasRedirected.current) {
      hasRedirected.current = true;

      if (user.role === 'renter') {
        window.location.href = '/renter/';
      } else {
        navigate('/manager/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, user, isLoading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(formData.username, formData.password);
      if (result.success) {
        setIsLoading(false);
      } else {
        setError(result.error || t('auth:error.loginFailed'));
        setIsLoading(false);
      }
    } catch (err) {
      setError(t('auth:error.unexpected'));
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-400/30 to-accent-400/30 rounded-full blur-3xl animate-float" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-accent-400/20 to-primary-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-primary-200/20 via-accent-200/20 to-primary-200/20 rounded-full blur-3xl" />
      </div>

      {/* Language toggle — top-right, outside the card */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageToggle />
      </div>

      <div className="w-full max-w-md relative z-10 animate-slide-up">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-accent-600 rounded-2xl transform rotate-6" />
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-accent-700 rounded-2xl" />
            <SparklesIcon className="h-10 w-10 text-white relative z-10" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('auth:welcomeTitle')}
          </h1>
          <p className="text-gray-500">
            {t('auth:welcomeSubtitle')}
          </p>
        </div>

        {/* Login Card */}
        <div className="glass-card-solid !p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="alert alert-error animate-fade-in">
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            {/* Username Field */}
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                {t('auth:usernameLabel')}
              </label>
              <div className="input-with-icon">
                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  className="form-input"
                  placeholder={t('auth:usernamePlaceholder')}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                {t('auth:passwordLabel')}
              </label>
              <div className="relative">
                <div className="input-with-icon">
                  <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                    className="form-input !pr-12"
                    placeholder={t('auth:passwordPlaceholder')}
                    disabled={isLoading}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me and Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-colors"
                />
                <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                  {t('common:auth.rememberMe')}
                </span>
              </label>
              <a href="#" className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors">
                {t('common:auth.forgotPassword')}
              </a>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary btn-lg w-full"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="spinner spinner-sm text-white"></div>
                  <span>{t('auth:signingIn')}</span>
                </div>
              ) : (
                <span>{t('common:auth.signIn')}</span>
              )}
            </button>
          </form>

        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-gray-500">
          {t('auth:footer')}
        </p>
      </div>
    </div>
  );
};

export default Login;
